// ======================================================================
// File: src/modules/ipe/costing/components/CostingFullScreenDrawer.js
// ======================================================================

import React, { useEffect, useState, useRef } from "react";
import html2pdf from "html2pdf.js";
import ipeCostingService from "../../services/ipeCostingService";

export default function CostingFullScreenDrawer({ enquiryId, onClose }) {

  const [data, setData] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);

  const printRef = useRef();

  // -----------------------------
  // Currency Formatter
  // -----------------------------
  const formatCurrency = (val) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR"
    }).format(val || 0);

  useEffect(() => {
    async function load() {
      try {
        const res = await ipeCostingService.getCostDashboard(enquiryId);
        const ver = await ipeCostingService.getCostVersions(enquiryId);

        setData(res || null);
        setVersions(ver || []);
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [enquiryId]);

  if (loading) return null;

  const summary = data?.summary || {};
  const items = data?.items || [];
  const materials = data?.materials || [];
  const operations = data?.operations || [];

  // -----------------------------
  // PRINT
  // -----------------------------
  const handlePrint = () => {
    window.print();
  };

  // -----------------------------
  // EXPORT PDF
  // -----------------------------
  const handleExportPDF = () => {
    const element = printRef.current;

    html2pdf().set({
      margin: 0.5,
      filename: `CostSheet_${enquiryId}.pdf`,
      html2canvas: { scale: 2 },
      jsPDF: { unit: "in", format: "a4", orientation: "portrait" }
    }).from(element).save();
  };

  return (
    <div style={{
      position: "fixed",
      top: 0,
      right: 0,
      width: "100%",
      height: "100%",
      background: "#f4f6f9",
      zIndex: 3000,
      overflowY: "auto",
      padding: 20
    }}>

      {/* ================= ACTION BAR ================= */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        marginBottom: 15
      }}>
        <div style={{ fontSize: 20, fontWeight: 600 }}>
          Engineering Cost Sheet
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <button className="btn btn-primary" onClick={handlePrint}>
            🖨 Print
          </button>

          <button className="btn btn-success" onClick={handleExportPDF}>
            ⬇ Export PDF
          </button>

          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>

      {/* ================= PRINT AREA ================= */}
      <div ref={printRef} className="print-area">

        {/* HEADER */}
        <div style={{
          background: "#1e293b",
          color: "#fff",
          padding: 15,
          borderRadius: 8,
          marginBottom: 20
        }}>
          <h4>Engineering Cost Sheet</h4>
          <div>Enquiry ID: {enquiryId}</div>
          <div>Version: V{summary.version_no}</div>
          <div>Status: {summary.status}</div>
        </div>

        {/* ================= VERSION HISTORY ================= */}
        <div className="card mb-4 shadow-sm">
          <div className="card-body">
            <h5 style={{ color: "#0d6efd" }}>Version History</h5>

            <table className="table table-bordered">
              <thead style={{ background: "#e9ecef" }}>
                <tr>
                  <th>Version</th>
                  <th>Status</th>
                  <th>Final Price</th>
                  <th>Created At</th>
                </tr>
              </thead>

              <tbody>
                {versions.map((v, i) => (
                  <tr key={i}>
                    <td><b>V{v.version_no}</b></td>
                    <td>
                      <span className={`badge ${
                        v.status === "approved"
                          ? "bg-success"
                          : v.status === "rejected"
                          ? "bg-danger"
                          : "bg-warning text-dark"
                      }`}>
                        {v.status}
                      </span>
                    </td>
                    <td>{formatCurrency(v.final_price)}</td>
                    <td>{v.created_at}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ================= SUMMARY ================= */}
        <div className="card mb-4 shadow-sm">
          <div className="card-body">
            <h5 style={{ color: "#198754" }}>Cost Summary</h5>

            <table className="table table-bordered">
              <tbody>
                <tr><td>Material Cost</td><td>{formatCurrency(summary.material_cost)}</td></tr>
                <tr><td>Machine Cost</td><td>{formatCurrency(summary.machine_cost)}</td></tr>
                <tr><td>Labour Cost</td><td>{formatCurrency(summary.labour_cost)}</td></tr>

                <tr className="table-light">
                  <td><b>Manufacturing Cost</b></td>
                  <td><b>{formatCurrency(summary.manufacturing_cost)}</b></td>
                </tr>

                <tr><td>Transport</td><td>{formatCurrency(summary.transport_charge)}</td></tr>
                <tr><td>Packing</td><td>{formatCurrency(summary.packing_charge)}</td></tr>
                <tr><td>Insurance</td><td>{formatCurrency(summary.insurance_charge)}</td></tr>

                <tr className="table-light">
                  <td><b>Subtotal</b></td>
                  <td><b>{formatCurrency(summary.subtotal)}</b></td>
                </tr>

                <tr><td>Margin</td><td>{formatCurrency(summary.margin_amount)}</td></tr>

                <tr className="table-primary">
                  <td><b>Selling Price</b></td>
                  <td><b>{formatCurrency(summary.selling_price)}</b></td>
                </tr>

                <tr><td>Tax</td><td>{formatCurrency(summary.tax_amount)}</td></tr>

                <tr className="table-success">
                  <td><b>Final Price</b></td>
                  <td><b>{formatCurrency(summary.final_price)}</b></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* ================= ITEM BREAKDOWN ================= */}
        <div className="card mb-4 shadow-sm">
          <div className="card-body">
            <h5 style={{ color: "#6f42c1" }}>Item Breakdown</h5>

            <table className="table table-bordered">
              <thead style={{ background: "#f1f3f5" }}>
                <tr>
                  <th>Item</th>
                  <th>Product</th>
                  <th>Equipment</th>
                  <th>Qty</th>
                  <th>Material</th>
                  <th>Machine</th>
                  <th>Labour</th>
                  <th>Total</th>
                </tr>
              </thead>

              <tbody>
                {items.map((i, idx) => (
                  <tr key={idx}>
                    <td>{i.item_seq_no}</td>
                    <td>{i.product_variant_name}</td>
                    <td>{i.equipment_name}</td>
                    <td>{i.quantity}</td>
                    <td>{formatCurrency(i.material_cost)}</td>
                    <td>{formatCurrency(i.machine_cost)}</td>
                    <td>{formatCurrency(i.labour_cost)}</td>
                    <td><b>{formatCurrency(i.total_cost)}</b></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ================= MATERIAL ================= */}
        <div className="card mb-4 shadow-sm">
          <div className="card-body">
            <h5 style={{ color: "#fd7e14" }}>Material Breakdown</h5>

            <table className="table table-bordered">
              <thead style={{ background: "#f8f9fa" }}>
                <tr>
                  <th>Material</th>
                  <th>Qty</th>
                  <th>Unit Cost</th>
                  <th>Total</th>
                </tr>
              </thead>

              <tbody>
                {materials.map((m, idx) => (
                  <tr key={idx}>
                    <td>{m.description}</td>
                    <td>{m.quantity}</td>
                    <td>{formatCurrency(m.unit_cost)}</td>
                    <td>{formatCurrency(m.total_cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ================= OPERATIONS ================= */}
        <div className="card mb-4 shadow-sm">
          <div className="card-body">
            <h5 style={{ color: "#dc3545" }}>Operation Breakdown</h5>

            <table className="table table-bordered">
              <thead style={{ background: "#f8f9fa" }}>
                <tr>
                  <th>Operation</th>
                  <th>Setup</th>
                  <th>Run</th>
                  <th>Machine</th>
                  <th>Labour</th>
                  <th>Total</th>
                </tr>
              </thead>

              <tbody>
                {operations.map((o, idx) => (
                  <tr key={idx}>
                    <td>{o.operation_name}</td>
                    <td>{o.setup_time}</td>
                    <td>{o.run_time}</td>
                    <td>{formatCurrency(o.machine_cost)}</td>
                    <td>{formatCurrency(o.labour_cost)}</td>
                    <td>{formatCurrency(o.total_cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
}